import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiFetch } from "@/lib/api";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { DetailsDrawer } from "@/components/details-drawer";
import { EntityForm } from "@/components/entity-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

interface CompanyDto { id: number; companyName: string; departmentName: string | null;
  accountsContactClientId: number | null; invoiceEmail: string | null;
  plainPackaging: boolean; hasApprovedCreditAccount: boolean; deleted: boolean; }
interface Paged<T> { items: T[]; totalCount: number; }

const schema = z.object({
  companyName: z.string().min(1).max(100),
  departmentName: z.string().max(100).nullable().optional(),
  accountsContactClientId: z.number().int().positive().nullable().optional(),
  invoiceEmail: z.string().email().or(z.literal("")).nullable().optional(),
  plainPackaging: z.boolean(),
  hasApprovedCreditAccount: z.boolean(),
});
type Form = z.infer<typeof schema>;

export function CompaniesPage() {
  const [page, setPage] = useState(0);
  const [editing, setEditing] = useState<CompanyDto | null>(null);
  const [creating, setCreating] = useState(false);
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["companies", page], queryFn: () => apiFetch<Paged<CompanyDto>>(`/api/companies?page=${page}&pageSize=50`) });

  const upsert = useMutation({
    mutationFn: (v: Form) => editing
      ? apiFetch<CompanyDto>(`/api/companies/${editing.id}`, { method: "PATCH", body: JSON.stringify(v) })
      : apiFetch<CompanyDto>("/api/companies", { method: "POST", body: JSON.stringify(v) }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["companies"] }); setEditing(null); setCreating(false); },
  });

  const cols: DataGridColumn<CompanyDto>[] = [
    { id: "name",  header: "Company", accessor: (r) => <button className="underline" onClick={() => setEditing(r)}>{r.companyName}</button> },
    { id: "dept",  header: "Department", accessor: (r) => r.departmentName ?? "—" },
    { id: "email", header: "Invoice email", accessor: (r) => r.invoiceEmail ?? "—" },
    { id: "credit", header: "Credit", accessor: (r) => r.hasApprovedCreditAccount ? "Yes" : "—" },
  ];

  return (
    <ListPage title="Companies" primaryAction={<IntentButton onClick={() => setCreating(true)}>New company</IntentButton>}>
      <DataGrid rows={data?.items ?? []} columns={cols} rowKey={(r) => r.id}
        totalRows={data?.totalCount ?? 0} pageIndex={page} pageSize={50} onPageChange={setPage} />
      <DetailsDrawer
        open={creating || editing !== null}
        onOpenChange={(o) => { if (!o) { setEditing(null); setCreating(false); } }}
        title={editing ? "Edit company" : "New company"}>
        <EntityForm schema={schema} defaultValues={editing ?? { companyName: "", departmentName: "", accountsContactClientId: null,
          invoiceEmail: "", plainPackaging: false, hasApprovedCreditAccount: false }} onSubmit={(v) => upsert.mutate(v)}>
          {(f) => (
            <div className="space-y-3">
              <div><Label htmlFor="cn">Name</Label><Input id="cn" {...f.register("companyName")} /></div>
              <div><Label htmlFor="dn">Department</Label><Input id="dn" {...f.register("departmentName")} /></div>
              <div><Label htmlFor="iv">Invoice email</Label><Input id="iv" type="email" {...f.register("invoiceEmail")} /></div>
              <div><Label htmlFor="ac">Accounts contact client ID</Label><Input id="ac" type="number" {...f.register("accountsContactClientId")} /></div>
              <label className="flex items-center gap-2"><input type="checkbox" {...f.register("plainPackaging")} /> Plain packaging</label>
              <label className="flex items-center gap-2"><input type="checkbox" {...f.register("hasApprovedCreditAccount")} /> Approved credit account</label>
              <Button type="submit" disabled={upsert.isPending}>{editing ? "Save" : "Create"}</Button>
            </div>
          )}
        </EntityForm>
      </DetailsDrawer>
    </ListPage>
  );
}
